import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import { countriesList, getCountryLabelByCode } from "../../../data/countriesList";
import "../Home/Homepage.css";
import "./KYCManagementPage.css";

const DEFAULT_PAGE_LIMIT = 20;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "KYC List";

const STATUS_TABS = [
  { key: "all", label: "All", apiStatus: "all" },
  { key: "pending", label: "Pending", apiStatus: "pending" },
  { key: "approved", label: "Approved", apiStatus: "approved" },
  { key: "rejected", label: "Rejected", apiStatus: "rejected" },
];

const formatDate = (iso) => {
  if (iso == null || iso === "") return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const getDocTypes = (row) => {
  const parts = [];
  if (row.id_document?.type) parts.push(row.id_document.type);
  if (row.tax_document?.type) parts.push(row.tax_document.type);
  if (row.selfie && (row.selfie.status || row.id_document)) parts.push("Selfie");
  return parts.length ? parts.join(", ") : "—";
};

const getRowCountryCode = (row) => {
  // KYC list returns ISO codes on the row (e.g. "IN"), not dial codes
  const code =
    row.country_code ??
    row.address?.country_code ??
    row.user?.country_code ??
    "";
  return code ? String(code).trim() : "";
};

const getRowCountry = (row) => {
  const named =
    row.country_name ??
    row.address?.country_name ??
    row.nationality ??
    row.country ??
    row.user?.country_name ??
    row.user?.country ??
    "";
  if (named && String(named).trim()) return String(named).trim();

  const code = getRowCountryCode(row);
  if (code) {
    const fromDial = getCountryLabelByCode(code);
    if (fromDial && fromDial !== "—" && fromDial !== code && fromDial !== `+${code}`) {
      return fromDial;
    }
    return code;
  }

  return "—";
};

const getUserEmail = (user = {}) => user.email ?? user.email_masked ?? "";
const getUserMobile = (user = {}) => user.mobile ?? user.mobile_masked ?? user.mobileNumber ?? "";

const rowToExport = (row) => {
  const user = row.user || {};
  const fullName = [row.first_name, row.last_name].filter(Boolean).join(" ");
  const countryCode = getRowCountryCode(row);
  const countryLabel = getRowCountry(row);
  return {
    "User Name": fullName,
    Email: getUserEmail(user),
    UUID: user.uuid ?? "",
    Mobile: getUserMobile(user),
    "Country Code": countryCode,
    Country: countryLabel === "—" ? "" : countryLabel,
    "User ID": row.user_id ?? user._id ?? "",
    "Document Type": getDocTypes(row) === "—" ? "" : getDocTypes(row),
    Status: row.overall_status ?? "",
    "Submitted At": row.submitted_at ?? "",
    "Record ID": row._id ?? "",
  };
};

const KYCManagementPage = () => {
  const navigate = useNavigate();
  const [kycList, setKycList] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_PAGE_LIMIT, total: 0, totalPages: 0 });

  const apiStatus = STATUS_TABS.find((t) => t.key === activeTab)?.apiStatus ?? "all";

  const buildParams = useCallback(
    (pageNum = pagination.page, limitNum = pagination.limit) => {
      const params = {
        status: apiStatus,
        page: pageNum,
        limit: limitNum,
      };
      if (searchDebounced.trim()) params.search = searchDebounced.trim();
      if (countryFilter.trim()) params.country_code = countryFilter.trim();
      return params;
    },
    [apiStatus, pagination.page, pagination.limit, searchDebounced, countryFilter]
  );

  const fetchCounts = useCallback(async () => {
    const reqs = STATUS_TABS.map((t) => {
      const params = { status: t.apiStatus, page: 1, limit: 1 };
      if (countryFilter.trim()) params.country_code = countryFilter.trim();
      return AdminService.getKycList(params);
    });
    const results = await Promise.all(reqs);
    const next = {};
    STATUS_TABS.forEach((t, i) => {
      next[t.key] = results[i]?.pagination?.total ?? 0;
    });
    setCounts(next);
  }, [countryFilter]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await AdminService.getKycList(buildParams());
    setLoading(false);
    if (res?.success) {
      setKycList(Array.isArray(res.data) ? res.data : []);
      const pag = res.pagination || {};
      setPagination((prev) => ({
        ...prev,
        total: pag.total ?? prev.total,
        totalPages: pag.total_pages ?? pag.totalPages ?? prev.totalPages,
      }));
      if (pag.total != null) setCounts((c) => ({ ...c, [activeTab]: pag.total }));
    } else {
      setError(res?.message || "Failed to load KYC list.");
      setKycList([]);
    }
  }, [buildParams, activeTab]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
  }, [apiStatus, searchDebounced, countryFilter, pagination.limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((p) => ({ ...p, page: newPage }));
  };

  const handlePageLimitChange = (e) => {
    setPagination((p) => ({
      ...p,
      limit: Number(e.target.value) || DEFAULT_PAGE_LIMIT,
      page: 1,
    }));
  };

  const fetchAllFilteredKyc = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getKycList(buildParams(currentPage, EXPORT_BATCH));
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.totalPages || res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllFilteredKyc();
      if (!allRows.length) {
        setToast({ type: "error", message: "No KYC records match your filters to export." });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);

      const tabLabel = STATUS_TABS.find((t) => t.key === activeTab)?.label?.toLowerCase() || "all";
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `kyc_${tabLabel}_${dateStamp}.xlsx`);

      setToast({
        type: "success",
        message: `Exported ${allRows.length} KYC record(s) to Excel.`,
      });
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error(e);
      setToast({
        type: "error",
        message: e?.message || "Failed to export Excel file.",
      });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const handleView = (row) => {
    const userId = row.user_id ?? row.user?._id ?? row.user?.id;
    if (userId) {
      navigate(`/dashboard/trader/${userId}?tab=kyc`, { state: { kycRow: row } });
    }
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved" || s === "verified") return "badge--kyc-approved";
    if (s === "rejected") return "badge--kyc-rejected";
    return "badge--kyc-pending"; // pending, submitted, etc.
  };

  const pageStart = kycList.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const pageEnd = kycList.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + kycList.length;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>KYC Management</span>
          </p>
          <h1 className="dashboard-hero__title">KYC Management</h1>
          <p className="dashboard-hero__subtitle">
            Review KYC submissions. Click View to open trader details and approve/reject.
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-id-card" />
        </div>
      </section>

      {toast && (
        <div className={`kyc-toast kyc-toast--${toast.type}`} role="status">
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        <section className="dashboard-activity-section">
          <div className="dashboard-activity-header kyc-activity-header">
            <div className="kyc-activity-header__left">
              <div className="dashboard-activity-header__icon">
                <i className="fas fa-redo" />
              </div>
              <h2 className="dashboard-activity-header__title">KYC Management</h2>
            </div>
            <div className="kyc-activity-header__actions">
              <span className="kyc-stat">
                <strong>{kycList.length}</strong> on page
                {pagination.total > 0 && (
                  <>
                    {" "}
                    (<strong>{pagination.total}</strong> total)
                  </>
                )}
              </span>
              {pagination.totalPages > 1 && (
                <span className="kyc-stat">
                  Page <strong>{pagination.page}</strong> / <strong>{pagination.totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="kyc-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered KYC records as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
              <button
                type="button"
                className="kyc-refresh-btn"
                onClick={fetchList}
                disabled={loading}
                title="Refresh"
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="kyc-filters">
            <input
              type="text"
              placeholder="Search email, UUID, mobile, name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="kyc-search"
            />
            <label className="kyc-country-filter">
              Country
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="kyc-country-filter__select"
              >
                <option value="">All countries</option>
                {countriesList.map((country, index) => (
                  <option key={`${country.value}-${index}`} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="kyc-page-limit">
              Rows per page
              <select
                value={pagination.limit}
                onChange={handlePageLimitChange}
                className="kyc-page-limit__select"
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="kyc-tabs">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`kyc-tab ${activeTab === tab.key ? "kyc-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                <span className="kyc-tab__count">{counts[tab.key] ?? 0}</span>
              </button>
            ))}
          </div>

          {error && (
            <div className="kyc-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div className="dashboard-content-inner kyc-content-inner">
            <div className="table-wrap kyc-table-wrap">
              <table className="data-table kyc-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Country</th>
                    <th>Country Code</th>
                    <th>Document Type</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && kycList.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="kyc-table__empty">
                        <i className="fas fa-spinner fa-spin" /> Loading…
                      </td>
                    </tr>
                  ) : kycList.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="kyc-table__empty">
                        No records in this category.
                      </td>
                    </tr>
                  ) : (
                    kycList.map((row) => {
                      const user = row.user || {};
                      const userLabel =
                        [getUserEmail(user), user.uuid, getUserMobile(user)].filter(Boolean).join(" · ") || "—";
                      const fullName = [row.first_name, row.last_name].filter(Boolean).join(" ") || "";
                      const displayUser = fullName ? `${fullName} · ${userLabel}` : userLabel;
                      const countryLabel = getRowCountry(row);
                      const countryCode = getRowCountryCode(row);
                      return (
                        <tr key={row._id}>
                          <td className="kyc-table__user">{displayUser}</td>
                          <td className="kyc-table__country">{countryLabel}</td>
                          <td className="kyc-table__country-code">
                            <code>{countryCode || "—"}</code>
                          </td>
                          <td>{getDocTypes(row)}</td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(row.overall_status)}`}>
                              {row.overall_status || "—"}
                            </span>
                          </td>
                          <td>{formatDate(row.submitted_at)}</td>
                          <td className="kyc-table__actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary kyc-view-btn"
                              onClick={() => handleView(row)}
                              title="View KYC in trader details"
                            >
                              <i className="fas fa-eye" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {!loading && pagination.total > 0 && (
              <div className="kyc-pagination">
                <span className="kyc-pagination__info">
                  Showing {pageStart}–{pageEnd} of {pagination.total}
                  {pagination.totalPages > 1 && (
                    <> · Page {pagination.page} of {pagination.totalPages}</>
                  )}
                </span>
                {pagination.totalPages > 1 && (
                  <div className="kyc-pagination__buttons">
                    <button
                      type="button"
                      className="kyc-pagination__btn"
                      disabled={pagination.page <= 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      <i className="fas fa-chevron-left" /> Prev
                    </button>
                    <button
                      type="button"
                      className="kyc-pagination__btn"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default KYCManagementPage;
